import { SplitText } from 'gsap/SplitText';

import DetectController from '../../assets/js/controllers/detect';
import ResizeController from '../../assets/js/controllers/resize';

export default class Gallery {

	static selector = 'gallery';

	el = {};

	captions = [];

	isDesktop;

	direction;

	timeout;

	isAnimating = false;
	isEnoughSlides = false;

	constructor(container) {
		this.el = {
			container,
			slides: container.querySelectorAll(`.${this.constructor.selector}__slides li`),
			captions: container.querySelectorAll(`.${this.constructor.selector}__caption`),
			paginationList: container.querySelector(`.${this.constructor.selector}__pagination ul`),
			paginationNums: container.querySelectorAll(`.${this.constructor.selector}__pagination ul li`),
			controls: container.querySelectorAll('[data-gl-control]')
		};

		this.isDesktop = DetectController.isDesktop;

		this.isEnoughSlides = this.el.slides.length > 1;

		if (this.isEnoughSlides) {
			this.paginationIndex = 1;
			this.realIndex = 0;

			for (const caption of this.el.captions) {
				this.captions.push(new SplitText(caption, {
					type: 'lines,words'
				}));
			}

			this.el.slides[0].classList.add('is-active');

			this.resetPagination();
			this.addEventListeners();
		}

	}

	addEventListeners() {
		for (const control of this.el.controls) {
			control.addEventListener('click', this.#onControlClick);

			if (this.isDesktop) {
				control.addEventListener('mouseenter', this.#onMouseEnter);
				control.addEventListener('mouseleave', this.#onMouseLeave);
			}
		}

		ResizeController.add(this.#onWindowResize);
	}

	removeEventListeners() {
		for (const control of this.el.controls) {
			control.removeEventListener('click', this.#onControlClick);

			if (this.isDesktop) {
				control.removeEventListener('mouseenter', this.#onMouseEnter);
				control.removeEventListener('mouseleave', this.#onMouseLeave);
			}
		}

		this.el.paginationList.removeEventListener('transitionend', this.#onPaginationStart);
		this.el.paginationList.removeEventListener('transitionend', this.#onPaginationEnd);

		ResizeController.remove(this.#onWindowResize);
	}

	#onWindowResize = () => {
		this.resetPagination();
	}

	#onPaginationEnd = () => {
		this.paginationIndex = 1;
		this.resetPagination();
		this.el.paginationList.removeEventListener('transitionend', this.#onPaginationEnd);
	}

	#onPaginationStart = () => {
		this.paginationIndex = this.el.paginationNums.length - 2;
		this.resetPagination();
		this.el.paginationList.removeEventListener('transitionend', this.#onPaginationStart);
	}

	#onControlClick = (e) => {
		if (!this.isAnimating) {
			this.isAnimating = true;

			const { currentTarget } = e;
			this.handleDirectionEvent(currentTarget, 'gallery:click');

			if (this.direction === 1) {
				this.paginationIndex++;
				this.realIndex++;
			} else {
				this.paginationIndex--;
				this.realIndex--;
			}

			this.handleCaptions();
			this.handlePagination();

			if (this.timeout) clearTimeout(this.timeout);
			this.timeout = setTimeout(() => {
				this.isAnimating = false;
			}, this.isDesktop ? 1125 : 650);
		}
	}

	#onMouseEnter = (e) => {
		const { currentTarget } = e;
		this.handleDirectionEvent(currentTarget, 'gallery:mouseenter');
	}

	#onMouseLeave = (e) => {
		const { currentTarget } = e;
		this.handleDirectionEvent(currentTarget, 'gallery:mouseleave');
	}

	handleDirectionEvent(target, customEvent) {
		this.direction = [...this.el.controls].indexOf(target);
		const event = new CustomEvent(customEvent, { detail: this.direction });
		this.el.container.dispatchEvent(event);
	}

	handleCaptions() {
		if (this.realIndex > this.el.slides.length - 1) {
			this.realIndex = 0;
		} else if (this.realIndex < 0) {
			this.realIndex = this.el.slides.length - 1;
		}

		const wasActive = this.el.container.querySelector(`.${this.constructor.selector}__slides li.is-active`);
		if (wasActive) wasActive.classList.remove('is-active');
		this.el.slides[this.realIndex].classList.add('is-active');
	}

	resetPagination() {
		this.el.paginationList.style.transform = `translateY(-${this.el.paginationNums[this.paginationIndex].offsetTop}px)`;
		this.el.paginationList.style.transitionDuration = '0ms';
	}

	handlePagination() {
		if (this.paginationIndex === this.el.paginationNums.length - 1) {
			this.el.paginationList.addEventListener('transitionend', this.#onPaginationEnd);
		} else if (this.paginationIndex === 0) {
			this.el.paginationList.addEventListener('transitionend', this.#onPaginationStart);
		}

		this.el.paginationList.style.transform = `translateY(-${this.el.paginationNums[this.paginationIndex].offsetTop}px)`;
		this.el.paginationList.style.transitionDuration = '600ms';
	}

	destroy() {
		if (this.timeout) clearTimeout(this.timeout);
		if (this.isEnoughSlides) this.removeEventListeners();
	}

}
